'use client'

import { useEffect, useRef, useState } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import { activeButtonStyle, inactiveButtonStyle } from '../helpers/helpers'

// A custom dropdown that replaces the native <select> in the Explore filter bar, so
// options can carry icons and the panel matches the app's pill styling. The parent
// owns the value and all guard logic (safe-mode, fetching, sign-in) and just gets
// the chosen value via `onSelect`. `active` toggles the trigger between the filled
// pill (a non-default value is set / an always-on control like the feed tabs) and
// the neutral pill (default value). Closes on outside-click, Escape, or selection.
export default function FilterDropdown({
  options,
  value,
  onSelect,
  active = false,
  className = '',
  ariaLabel,
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const selected = options.find((o) => o.value === value) || options[0]
  const SelectedIcon = selected?.Icon

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className={`${
          active ? activeButtonStyle : inactiveButtonStyle
        } flex h-9 w-full items-center gap-2 rounded-full pl-4 pr-3 text-sm font-semibold`}
      >
        {SelectedIcon && <SelectedIcon className="shrink-0 text-[15px]" />}
        <span className="min-w-0 flex-1 truncate text-center">
          {selected?.label}
        </span>
        <FaChevronDown
          className={`shrink-0 text-xs opacity-70 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 z-30 mt-2 max-h-72 overflow-auto rounded-2xl border border-white/10 bg-[var(--foreground-bg-color)] p-1.5 shadow-xl"
        >
          {options.map((o) => {
            const isActive = o.value === value
            const OptIcon = o.Icon
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setOpen(false)
                  onSelect(o.value)
                }}
                className={`flex w-full items-center gap-2.5 rounded-full px-4 py-2.5 text-left text-sm font-semibold transition ${
                  isActive
                    ? 'bg-[var(--main-bg-color)] text-white'
                    : 'text-gray-200 hover:bg-white/10'
                }`}
              >
                {OptIcon && <OptIcon className="shrink-0 text-[15px]" />}
                <span>{o.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
